import {
  createContext,
  useContext,
  useRef,
  useState,
  type ReactNode,
  type ReactElement,
  type MutableRefObject,
} from 'react';

import { _getLogger } from '@doxyme/analytics';
import {
  type ExtensionRegistry,
  type ExtensionBridge,
  logErrorAndThrowBind,
  type LogErrorAndThrowBound,
} from '@doxyme/extensions-bridge';
import { type Application } from '@doxyme/extensions-graphql-types';

const logger = _getLogger('useRegistry', {
  component: 'extensions',
  lib: 'bridge-react',
});

const logErrorAndThrow: LogErrorAndThrowBound = logErrorAndThrowBind(logger);

type OnRegistryUpdate = () => Promise<void>;

type RegistryDependencies = {
  bridge: ExtensionBridge;
  registry: ExtensionRegistry;
  onRegistryUpdate: OnRegistryUpdate;
  locale?: string;
};

export type UseRegistryReturnValue = {
  apps: Application[];
  isLoaded: boolean;
  setDependencies: (deps: RegistryDependencies) => void;
  activate: (id: string) => Promise<void>;
  deactivate: (id: string) => Promise<void>;
  saveConfiguration: (
    id: string,
    config: Record<string, unknown>,
  ) => Promise<void>;
  generateAppToken: (id: string) => Promise<string>;
  refresh: () => Promise<void>;
  isInitialized: boolean;
};

const hookInitValues: UseRegistryReturnValue = {
  apps: [],
  isLoaded: false,
  setDependencies: (): void => undefined,
  activate: async (): Promise<void> => undefined,
  deactivate: async (): Promise<void> => undefined,
  saveConfiguration: async (): Promise<void> => undefined,
  generateAppToken: async (): Promise<string> => '',
  refresh: async (): Promise<void> => undefined,
  isInitialized: false,
};

// Create the Context
const RegistryContext = createContext<UseRegistryReturnValue>(hookInitValues);

const checkRef = <T,>(
  ref: MutableRefObject<T | undefined>,
  name: string,
): T => {
  if (!Boolean(ref.current)) {
    logErrorAndThrow(
      `[useRegistrySingleton] ${name} dependency has not been set`,
    );
  }
  return ref.current as T;
};

// Provider Component
export function RegistryProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const [apps, setApps] = useState<Application[]>([]);

  const appsRef = useRef<Application[]>([]);

  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const bridgeRef = useRef<ExtensionBridge>(undefined);
  const registryRef = useRef<ExtensionRegistry>(undefined);
  const onRegistryUpdateRef = useRef<OnRegistryUpdate>(undefined);
  const localeRef = useRef<string>(undefined);

  const setDependencies = ({
    bridge,
    registry,
    onRegistryUpdate,
    locale,
  }: RegistryDependencies): void => {
    bridgeRef.current = bridge;
    registryRef.current = registry;
    onRegistryUpdateRef.current = onRegistryUpdate;
    localeRef.current = locale;
  };

  const setAppsAndUpdateRef = (updatedApps: Application[]): void => {
    // Create a new array reference to ensure React detects the change
    appsRef.current = updatedApps;
    setApps(updatedApps);
  };

  const updateBridge = (appId: string, add = false): void => {
    const bridge = checkRef(bridgeRef, 'Bridge');
    const app = appsRef.current.find(({ id }) => id === appId);
    if (!app?.manifest) {
      logger.error(
        '[useRegistrySingleton] Could not update bridge - manifest not defined',
      );
      return;
    }
    if (add) {
      bridge.addInstance(app.manifest);
    } else {
      bridge.removeInstance(app.manifest.id);
    }
  };

  const retrieveData = async (): Promise<void> => {
    const registry = checkRef<ExtensionRegistry>(registryRef, 'Registry');
    const locale = checkRef(localeRef, 'Locale');

    const applications = await registry.getApplications({
      iso: locale,
    });

    const bridge = checkRef(bridgeRef, 'Bridge');

    // Remove all instances except those that are sideloaded (by source)
    const instanceIds = bridge.getInstanceIds();
    for (const id of instanceIds) {
      try {
        const instance = bridge.getInstance(id);
        if (instance.source !== 'sideload') {
          bridge.removeInstance(id);
        }
      } catch {
        // Instance may have been removed already, ignore
      }
    }

    // Collect names of sideloaded extensions to exclude from registry additions
    const sideloadedNames = new Set<string>();
    for (const id of bridge.getInstanceIds()) {
      try {
        const instance = bridge.getInstance(id);
        if (instance.source === 'sideload') {
          sideloadedNames.add(instance.manifest.name);
        }
      } catch {
        // Instance may have been removed, ignore
      }
    }

    // Add installed apps, excluding those that are sideloaded (by name)
    const manifestsToAdd = applications
      .filter(
        (app) => app.isInstalled && !sideloadedNames.has(app.manifest.name),
      )
      .map(({ manifest }) => manifest);

    bridge.addAllInstances(manifestsToAdd);

    setAppsAndUpdateRef(applications);

    setIsLoaded(true);
  };

  const activate = async (appId: string): Promise<void> => {
    const registry = checkRef(registryRef, 'Registry');

    await registry.activate(appId);

    updateBridge(appId, true);
    setAppsAndUpdateRef(
      appsRef.current.map((app) =>
        app.id === appId ? { ...app, isInstalled: true } : app,
      ),
    );

    const onRegistryUpdate = checkRef(onRegistryUpdateRef, 'onRegistryUpdate');
    await onRegistryUpdate();
  };

  const deactivate = async (appId: string): Promise<void> => {
    const registry = checkRef(registryRef, 'Registry');

    await registry.deactivate(appId);

    updateBridge(appId);
    setAppsAndUpdateRef(
      appsRef.current.map((app) =>
        app.id === appId ? { ...app, isInstalled: false } : app,
      ),
    );

    const onRegistryUpdate = checkRef(onRegistryUpdateRef, 'onRegistryUpdate');
    await onRegistryUpdate();
  };

  const saveConfiguration = async (
    appId: string,
    config: Record<string, unknown>,
  ): Promise<void> => {
    const registry = checkRef(registryRef, 'Registry');

    const app = appsRef.current.find(({ id }) => id === appId);
    if (!app) {
      logger.error(`[saveConfiguration] App was not found for appId: ${appId}`);
      return;
    }

    const params = { extensionId: appId, config };
    await (Boolean(app.config)
      ? registry.updateConfiguration(params)
      : registry.createConfiguration(params));

    setAppsAndUpdateRef(
      appsRef.current.map((a) => (a.id === appId ? { ...a, config } : a)),
    );
  };

  const generateAppToken = async (appId: string): Promise<string> => {
    const registry = checkRef(registryRef, 'Registry');
    const tokenResponse = await registry.generateAppToken({
      extensionId: appId,
    });
    return tokenResponse.token;
  };

  const value: UseRegistryReturnValue = {
    apps,
    isLoaded,
    setDependencies,
    activate,
    deactivate,
    saveConfiguration,
    generateAppToken,
    refresh: retrieveData,
    isInitialized: true,
  };

  return (
    <RegistryContext.Provider value={value}>
      {children}
    </RegistryContext.Provider>
  );
}

// Hook to use the Registry context
export function useRegistrySingleton(): UseRegistryReturnValue {
  const context = useContext(RegistryContext);
  if (!context) {
    throw new Error(
      'useRegistrySingleton must be used within RegistryProvider',
    );
  }
  return context;
}
